<script setup lang="ts">
import MenuList from '@layout/nav-v2/mobile/components/MenuList.vue';
import Divider from '@components/Divider.vue';
import SubmenuItem from '@layout/nav-v2/SubmenuItem.vue';
import { trans } from '@elements/translation.ts';
import { computed } from 'vue';
import Typography from '@components/Typography.vue';
import Icon from '@components/Icon.vue';
import IconPhoneCall from '@icons/ph_phone-call.svg';
import IconPhoneInbound from '@icons/ph_phone-inbound.svg';
import IconChatText from '@icons/ph_chat-text.svg';
import IconEnvelope from '@icons/ph_envelope.svg';
import IconQuestionCircle from '@icons/ph_question-circle.svg';
import { storeToRefs } from 'pinia';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import { PhoneNumber } from '@tools/phoneNumber.ts';
import { URL_CONTACT } from '@tools/routes/routes';
import { showNewMessage } from '@intercom/messenger-js-sdk';

const {
  calendlyUrl,
  faqLink,
  salesPhone,
  internationalSalesPhone,
} = storeToRefs(useNavbarStore());

const emit = defineEmits<{(e: 'intercom-open'): void }>();

const companyName = computed<string>(() => window.COMPANY_NAME);

const openIntercom = (): void => {
  showNewMessage('');
  emit('intercom-open');
};
</script>

<template>
  <MenuList class="space-y-2">
    <li>
      <SubmenuItem
        :link="`tel:${internationalSalesPhone}`"
        class="!h-12"
      >
        <div class="desktop-help-menu__submenu-item">
          <Icon
            :svg="IconPhoneCall"
            width="24"
            height="24"
            fill="gray-darker"
          />

          <div class="flex items-center space-x-1">
            <Typography variant="body">
              {{ trans('user_menu.call_company', { company_name: companyName }) }}
            </Typography>

            <Typography variant="body-bold">
              • {{ salesPhone }}
            </Typography>
          </div>
        </div>
      </SubmenuItem>
    </li>

    <li>
      <SubmenuItem
        :link="calendlyUrl ?? undefined"
        class="!h-12"
      >
        <div class="desktop-help-menu__submenu-item">
          <Icon
            :svg="IconPhoneInbound"
            width="24"
            height="24"
            stroke="gray-darker"
          />

          <Typography variant="body">
            {{ trans('user_menu.recalled') }}
          </Typography>
        </div>
      </SubmenuItem>
    </li>

    <li>
      <SubmenuItem
        class="!h-12"
        @click="openIntercom"
      >
        <div class="desktop-help-menu__submenu-item">
          <Icon
            :svg="IconChatText"
            width="24"
            height="24"
            fill="gray-darker"
          />

          <Typography variant="body">
            {{ trans('user_menu.chat') }}
          </Typography>
        </div>
      </SubmenuItem>
    </li>

    <li>
      <SubmenuItem
        :link="URL_CONTACT"
        class="!h-12"
      >
        <div class="desktop-help-menu__submenu-item">
          <Icon
            :svg="IconEnvelope"
            width="24"
            height="24"
            fill="gray-darker"
          />

          <Typography variant="body">
            {{ trans('user_menu.send_message') }}
          </Typography>
        </div>
      </SubmenuItem>
    </li>

    <li class="px-3">
      <Divider class="my-4" color="gray" />
    </li>

    <li>
      <SubmenuItem
        :link="faqLink ?? undefined"
        class="!h-12"
      >
        <div class="desktop-help-menu__submenu-item">
          <Icon
            :svg="IconQuestionCircle"
            width="24"
            height="24"
            fill="gray-darker"
          />

          <Typography variant="body">
            {{ trans('user_menu.faq') }}
          </Typography>
        </div>
      </SubmenuItem>
    </li>
  </MenuList>
</template>

<style scoped lang="scss">
.desktop-help-menu {
  &__submenu-item {
    @apply flex items-center space-x-2;
  }
}
</style>
